import { useCallback } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { insertTrackEvent, trackHeaderEvent } from '@comeon/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { ROUTE_ACCOUNT_DEPOSIT, ROUTE_MOBILE_QUICK_DEPOSIT } from '~/components/router/constants';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import { showNotification } from '~/components/notifications/ducks/notifications';
import useTranslations from '~/components/translation/use-translations';
import { selectIsEventLive } from '~/components/we-spin/old-flow/ducks/we-spin-dashboard-duck';
import { WE_SPIN_WALLET_TEXT_KEYS } from '~/components/we-spin/new-flow/constants/we-spin-dashboard-text-keys';
import { getCookies } from '~/store/cookies.js';

import Anchor from '~/components/misc/anchor.js';
import breakpoint from '~/utils/style-utils.ts';
import useGlobalQuickDeposit from '../../hooks/use-global-quick-deposit';

const WHILE_TAP_SCALE = { scale: 0.9 };

const LinkWithEffect = props => {
	return (
		<motion.span whileTap={WHILE_TAP_SCALE}>
			<Anchor {...props}>{props.children}</Anchor>
		</motion.span>
	);
};

const AccountDisplay = styled.dl`
	align-items: center;
	background-color: var(--balance-circle, rgba(var(--base-08), 1));
	display: inline-flex;
	padding: 2px;
	margin: 0;
	border-radius: 50%;
`;

const AccountIconWrapper = styled.dt`
	align-items: center;
	background-color: var(--balance-header-icon, rgba(var(--base-09), 1));
	display: flex;
	justify-content: center;
	width: 24px;
	height: 24px;
	${breakpoint.lg`
	width: 32px;
	height: 32px;
	`};
	svg {
		fill: var(--balance-icons, rgba(var(--base-09), 1));
		width: 17px;
		height: 17px;
		${breakpoint.lg`
	width: 22px;
	height: 22px;
	`};
	}
	border-radius: 50%;
`;

export const HeaderIconWithEffect = props => {
	const router = useRouter();
	const contextualPathTransformer = useContextualPathTransformer();

	return (
		<AccountDisplay>
			<AccountIconWrapper {...props}>
				<AccountIcon whileTap={WHILE_TAP_SCALE}>
					<Anchor
						onClick={() => {
							router.push(contextualPathTransformer('/account'));
							trackHeaderEvent('Account icon');
						}}
						{...props}
					>
						{props.children}
					</Anchor>
				</AccountIcon>
			</AccountIconWrapper>
		</AccountDisplay>
	);
};

const AccountIcon = styled(motion.span)`
	a {
		color: var(--account-text, rgba(var(--base-01), 1));
		display: flex;
		flex-direction: column;
		align-items: center;
		font: var(--caption);
		${breakpoint.lg`
			margin-left: 20px;
			min-width: 44px;
			text-align: center;
		`};
		white-space: nowrap;
	}
	svg {
		align-self: center;
		fill: var(--account-icon, rgba(var(--base-01), 1));
	}
`;

export const QuickDepositLink = ({ children, className = '', onClick = e => e, ref }) => {
	const dispatch = useDispatch();
	const router = useRouter();
	const contextualPathTransformer = useContextualPathTransformer();
	const isEventLive = useSelector(selectIsEventLive);
	const isDepositBlocked = useSelector(state => state.account.isDepositBlocked);
	const { isQuickDepositAvailable } = useGlobalQuickDeposit(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const handleDepositClick = useCallback(async () => {
		const paymentPixelData = {
			pixel_screen: `${getCookies('lastProduct')}-page`,
			pixel_deposit_action: 'initiate-deposit-header'
		};

		if (isQuickDepositAvailable) {
			searchParams.set('modal', ROUTE_MOBILE_QUICK_DEPOSIT);
			setSearchParams(searchParams);
			insertTrackEvent({
				category: 'Header',
				action: 'Click - Balance icon',
				label: 'Quick_deposit'
			});
			return;
		}

		router.push(contextualPathTransformer(ROUTE_ACCOUNT_DEPOSIT), {
			state: { paymentPixelData }
		});
		trackHeaderEvent('Balance icon');
	}, [isQuickDepositAvailable, router, contextualPathTransformer, searchParams, setSearchParams]);

	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_WALLET_TEXT_KEYS);

	const handleLinkClick = useCallback(
		e => {
			if (isEventLive) {
				dispatch(
					showNotification({
						message: tr.WE_SPIN_WALLET_BLOCKED_DESC,
						color: 'red',
						autoHide: true,
						openInSidebar: false
					})
				);
				return;
			}
			handleDepositClick();
			onClick?.(e);
		},
		[isEventLive, handleDepositClick, onClick, dispatch, tr.WE_SPIN_WALLET_BLOCKED_DESC]
	);

	if (isLoadingTranslations) {
		return null;
	}

	return (
		<LinkWithEffect
			disabled={isDepositBlocked}
			className={className}
			onClick={handleLinkClick}
			data-at="deposit-icon-header"
			ref={ref}
		>
			{children}
		</LinkWithEffect>
	);
};
